import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useLogto } from '@logto/react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout, setBridgeAuthenticated, setSdkAuthenticated, setStatus, setUser } from '@/store/auth.slice';
import {
  clearStoredTokens,
  dbg,
  consumeAuthStartParam,
  getLogtoAccessToken,
  getRedirectUri,
  hasStoredAccessToken,
  hasStoredSession,
  isTelegramWebView,
  LAST_CONSUMED_AUTH_CODE,
  setAccessTokenGetter,
  setSessionExpiredHandler,
  startTelegramSignIn,
  isGrantDeclaredDead,
} from '@/lib/logto';
import { useGetMeQuery } from '@/services/endpoints/auth';
import { useGetLimuloMeQuery } from '@/services/limuloApi';
import { limuloMeToAuthUser } from '@/lib/limulo/identity';
import { useNicknameQgammon } from '@/hooks/useNicknameQgammon';
import { revocaEChiudiLimuloSession } from '@/lib/limulo/session';
import { getLogtoRolesFromAccessToken } from '@/lib/logto';
import { env } from '@/env';

/**
 * Drives sign-in in two contexts:
 *
 *  - **Standalone web** (browser tab, Telegram dev mock): standard Logto SDK
 *    redirect flow. `useHandleSignInCallback` on `/auth/callback` finishes it.
 *  - **Real Telegram WebView**: Logto's interactive flow can't run inside the
 *    embedded WebView (Google blocks OAuth there). Instead `startTelegramSignIn`
 *    opens the system browser via `WebApp.openLink`. The bridge in
 *    `<AuthCallback>` deep-links back via `t.me/.../app?startapp=auth_<code>`,
 *    and on relaunch `consumeAuthStartParam` exchanges the code for tokens.
 *
 * Auth state lives in Redux (`state.auth.bridgeAuthenticated`) so all
 * components that read `useLogtoAuth()` stay in sync — particularly important
 * for sign-out, where the trigger lives in <Profile>/<Settings> but the gate
 * lives in <AppShell>.
 *
 * Resume handling: when Telegram brings the existing Mini App back to
 * foreground via the t.me deep link (instead of fully re-launching it), no
 * component re-mounts, so a plain mount-time effect would miss the new
 * `auth_<code>` startParam. We re-run the consume on `visibilitychange` /
 * `focus` events and dedup by storing the last consumed code in localStorage.
 */
const LAST_CONSUMED_CODE = LAST_CONSUMED_AUTH_CODE;

export function useLogtoAuth() {
  const dispatch = useAppDispatch();
  const inTelegram = isTelegramWebView();

  const {
    isAuthenticated: sdkAuthenticated,
    isLoading,
    getAccessToken,
    signIn,
    signOut,
    clearAllTokens,
  } = useLogto();

  const bridgeAuthenticated = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const [bridgeChecked, setBridgeChecked] = useState(!inTelegram);

  // La sessione dell'SDK nello store, perche' `haSessione` (lib/ospite.ts) la veda insieme al bridge.
  // LAYOUT effect, non passivo: gira prima di ogni `useEffect` del commit — compresi quelli con
  // cui RTK Query sottoscrive le query dei figli — quindi la guardia dell'ospite non vede mai uno
  // stato piu' vecchio di quello con cui questo componente ha appena reso.
  useLayoutEffect(() => {
    dispatch(setSdkAuthenticated(sdkAuthenticated));
  }, [sdkAuthenticated, dispatch]);

  useEffect(() => {
    if (!inTelegram) return;

    let cancelled = false;
    let running = false;

    const tryConsume = async (reason: string): Promise<void> => {
      if (running) return;
      if (hasStoredAccessToken()) {
        dispatch(setBridgeAuthenticated(true));
        setBridgeChecked(true);
        return;
      }
      running = true;
      try {
        // Stale access token but a persisted session (refresh_token present) →
        // silently refresh rather than bouncing to Login. Keeps the user signed
        // in across reboots/refreshes; only an explicit logout (which clears the
        // refresh_token) requires re-auth. If the refresh fails (dead token) we
        // clear the session and fall through to the sign-in path → clean Login,
        // not a wall of 401s.
        if (hasStoredSession()) {
          const refreshed = await getLogtoAccessToken();
          if (cancelled) return;
          if (refreshed) {
            dispatch(setBridgeAuthenticated(true));
            setBridgeChecked(true);
            return;
          }
          // 🔴 QUI SI PERDEVANO LE SESSIONI, E BASTAVA UN SOLO RINNOVO FALLITO.
          //
          // Fino al 06/09 questo ramo faceva `clearStoredTokens()` **incondizionato**. Ma
          // `getLogtoAccessToken()` torna `null` per QUALUNQUE motivo — inclusa una rete
          // che cade a meta' chiamata: `redeemOnce` intercetta l'errore e torna
          // `{accessToken: null}` (`logto.ts`, ramo `refresh network error`) **senza
          // cancellare niente**, ed era questa riga a cancellare tutto dopo.
          // ⇒ E `clearStoredTokens()` azzera anche `qlash.limulo.session`, che sta in
          // `PER_SERVICE_TOKEN_KEYS`: **un singolo `connectionfailed` su `oidc/token`
          // spazzava via identita' Logto E sessione Limulo insieme.**
          //
          // 📌 RIPRODOTTO, non dedotto (or45, 06/09 10:33Z): **una sola** chiamata
          // `POST oidc/token` abortita → `refresh_token`, `access_token` e
          // `qlash.limulo.session` tutti spariti, **zero `invalid_grant`**, e in
          // `bridge_debug.v2` la confessione di questa riga:
          // *«sessione cancellata: c era una sessione persistita ma il refresh non ha
          // reso un token»*.
          //
          // 🔑 LA CORREZIONE: il teardown di un grant DAVVERO morto lo possiede gia'
          // `handleDeadGrant` (che cancella **e** chiama `onSessionExpired`). Duplicarlo
          // qui era inutile nel caso terminale e distruttivo in quello transitorio.
          // ⇒ Si cancella **solo** se il grant e' stato dichiarato morto; altrimenti la
          // sessione RESTA e il prossimo tentativo puo' guarire da solo.
          //
          // ⚠️ Il verso in cui si sbaglia non e' simmetrico: tenere una sessione morta
          // costa una schermata di Login in piu'; buttarne una viva costa all'utente il
          // lavoro in corso e un login vero.
          if (isGrantDeclaredDead()) {
            dbg('sessione cancellata: il grant e stato dichiarato morto');
            clearStoredTokens();
          } else {
            dbg('nessun token da questo giro, ma il grant NON e dichiarato morto: la sessione resta');
          }
          dispatch(setBridgeAuthenticated(false));
        }
        // Peek startParam first to dedup repeat invocations on the same code.
        let startParam: string | undefined;
        try {
          const { retrieveLaunchParams } = await import('@tma.js/sdk-react');
          startParam = retrieveLaunchParams().tgWebAppStartParam;
        } catch {
          /* ignore */
        }
        const code = startParam?.startsWith('auth_') ? startParam.slice(5) : null;
        if (code && localStorage.getItem(LAST_CONSUMED_CODE) === code) {
          console.info('[auth] skipping already-consumed code (', reason, ')');
          setBridgeChecked(true);
          return;
        }
        console.info('[auth] attempting consume (', reason, ') startParam=', startParam?.slice(0, 24));
        const result = await consumeAuthStartParam();
        if (cancelled) return;
        if (result === true) {
          if (code) localStorage.setItem(LAST_CONSUMED_CODE, code);
          dispatch(setBridgeAuthenticated(true));
        } else {
          // Nothing restored a session (no fresh token, refresh failed/absent,
          // no sign-in code) → ensure the gate shows Login.
          dispatch(setBridgeAuthenticated(false));
        }
        setBridgeChecked(true);
      } catch (err) {
        // Anything thrown in here used to leave the gate stuck on "Loading…"
        // FOREVER: the finally only reset `running`, so `bridgeChecked` stayed
        // false and `effectiveLoading` never flipped. A refresh call that
        // rejects (network, Logto 5xx, a dead-grant handler that throws) was
        // enough. Observed 2026-07-26 from the Telegram app: "Loading…" and
        // nothing else, no error on screen because nothing rendered.
        //
        // We do NOT swallow it silently: log it, and let the finally below open
        // the gate to Login — a sign-in screen the user can act on beats a
        // spinner that never ends.
        console.error('[auth] consume failed (', reason, '):', err);
        if (!cancelled) dispatch(setBridgeAuthenticated(false));
      } finally {
        running = false;
        // The single guarantee that matters: whatever happened above — success,
        // early return, throw — the auth check is OVER. Every other path that
        // sets this is now redundant, and that is on purpose: this is the one
        // that cannot be forgotten by a future branch.
        if (!cancelled) setBridgeChecked(true);
      }
    };

    void tryConsume('mount');

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void tryConsume('visibilitychange');
    };
    const onFocus = (): void => void tryConsume('focus');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [inTelegram, dispatch]);

  // Expose getAccessToken to non-React callers (RTK Query). The wrapper in
  // `lib/logto.ts` prefers our manually-stored token, so this getter is only
  // used in the standalone web fallback.
  useEffect(() => {
    setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // When the shared refresh grant dies (terminal 400 invalid_grant on any
  // per-service redemption), logto.ts has already wiped the session — drop the
  // gate to a clean Login instead of leaving every section 401ing / masked as
  // the shared "Dev" user. This is the self-healing path for the recurring
  // account-switch dead-grant bug.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      dispatch(logout());
    });
  }, [dispatch]);

  const isAuthenticated = bridgeAuthenticated || sdkAuthenticated;
  const effectiveLoading = inTelegram ? !bridgeChecked && !bridgeAuthenticated : isLoading;

  // Reflect loading state in Redux so common components can show spinners.
  useEffect(() => {
    dispatch(setStatus(effectiveLoading ? 'loading' : 'idle'));
  }, [effectiveLoading, dispatch]);

  // Pull the QLASH user record once authenticated — da Limulo o da Laravel, MAI da
  // entrambi.
  //
  // 🔑 Perche' e' una scelta di BUILD e non un fallback. Fino al 2026-09-05 questa riga
  // chiamava sempre il monolite Laravel (`GET /users/me`), dietro nessun flag: era
  // l'ultimo pezzo del boot che legava a Laravel anche una build in cui Laravel non
  // serve piu' (decisione di Luca 05/09: «l'app deve prendere solo dati Limulo e i suoi
  // nuovi servizi»). Il costo era misurabile, non teorico: con il monolite di staging
  // giu' (503 da API Gateway, misurato su `stg-tma` il 05/09) la sezione `/limulo/*`
  // mostrava dati veri mentre il boot bruciava ~19 s di rete per schermata su una
  // chiamata di cui non aveva bisogno.
  //
  // ⚠️ NON e' «prova Limulo, se fallisce ripiega su Laravel». Un ripiego qui sarebbe un
  // guasto d'identita': i due backend rispondono con DUE SPAZI DI ID diversi (`id`
  // numerico Laravel contro ObjectId stringa Limulo), e uno store popolato ora dall'uno
  // ora dall'altro darebbe a valle due chiavi di giunzione incompatibili senza che
  // nessuna schermata se ne accorga. La build sceglie una volta, all'accensione del
  // flag, e resta su quella.
  //
  // Le due query sono dichiarate entrambe (le hook di RTK Query non si chiamano dentro
  // un ramo) e si escludono a vicenda con `skip`: quella spenta non fa rete.
  const limuloOwnsIdentity = env.featureLimuloEnabled;
  const { data: laravelMe } = useGetMeQuery(undefined, {
    skip: !isAuthenticated || limuloOwnsIdentity,
  });
  const { data: limuloMe } = useGetLimuloMeQuery(undefined, {
    skip: !isAuthenticated || !limuloOwnsIdentity,
  });
  useEffect(() => {
    // I ruoli si leggono QUI e si passano come parametro: `limuloMeToAuthUser` e' pura
    // apposta, cosi' `tools/verify-limulo.ts` puo' esercitarla sotto Node.
    const next = limuloOwnsIdentity
      ? limuloMeToAuthUser(limuloMe, getLogtoRolesFromAccessToken())
      : laravelMe;
    if (next) dispatch(setUser(next));
  }, [limuloOwnsIdentity, limuloMe, laravelMe, dispatch]);

  // Il nome pubblico del backgammon (I-898): il nickname QLASH, non `username` (v. l'hook).
  useNicknameQgammon(limuloOwnsIdentity);

  const triggerSignIn = useCallback(() => {
    if (inTelegram) void startTelegramSignIn();
    else void signIn(getRedirectUri());
  }, [inTelegram, signIn]);

  const triggerSignOut = useCallback(() => {
    // 🔴 L'ALTRO ramo che cancella la sessione, e prima di oggi non scriveva
    // NIENTE — ne' in console ne' nel registro.
    // [Decisione di Luca, 2026-09-07, L-455: «Aggiungi riga in entrambi i rami».]
    // ⚠️ Le due righe dicono cose DIVERSE apposta: qui l'utente ha chiesto di
    // uscire, li' (`handleDeadGrant`) la sessione e' stata uccisa da un rifiuto
    // del server. Una riga sola per entrambi renderebbe indistinguibili un
    // logout voluto e una disconnessione subita — che e' esattamente la domanda
    // a cui il registro deve rispondere quando qualcuno si ritrova fuori senza
    // sapere perche'.
    // ⚠️ Il FATTO, mai il contenuto: nessun token, nessun frammento, nessuna
    // chiave. E PRIMA della cancellazione: v. la nota in `handleDeadGrant`.
    dbg('[signout] uscita richiesta dall’utente: cancello la sessione');
    // Flip the Redux gate immediately so the UI drops to <Login /> right away.
    dispatch(logout());
    // 🔴 DENTRO TELEGRAM NON SI CHIAMA `signOut()` DELL'SDK — nemmeno senza argomenti.
    // Qui c'era scritto «without a post-logout URI just clears tokens — no redirect»: FALSO.
    // `@logto/client` 3.1.8 `signOut()` revoca, pulisce e poi chiama SEMPRE
    // `adapter.navigate(endSessionUri)` (`lib/client.js:234-252`), che in `@logto/browser` e'
    // `window.location.assign` (`lib/index.js:8-9`); l'URI di ritorno e' solo facoltativo. Il
    // commento di `@logto/react` lo dice da se': «since the location will be redirected».
    // ⇒ nella WebView la mini app veniva sostituita dalla pagina «signed out» di Logto e non
    //   c'era modo di tornare se non chiudendo Telegram (reperto del collaudo 2026-09-23, Luca:
    //   «una pagina che non muore»).
    // In Telegram l'SDK non ha comunque una sessione (il login passa dal ponte PKCE, che scrive
    // le NOSTRE chiavi, non le `logto:`), e il cookie di Logto vive nel browser di sistema: la
    // navigazione non chiudeva niente che servisse chiudere. Basta `clearAllTokens()`, che
    // pulisce senza navigare. Fuori da Telegram resta `signOut()`, com'era.
    // L'ordine resta quello di prima: `clearStoredTokens()` DOPO che l'SDK ha finito, cosi'
    // la nostra pulizia e' l'ultima a scrivere e nessuna chiave `logto:` tardiva sopravvive.
    //
    // 🔴 MA QUELLA NAVIGAZIONE FACEVA ANCHE UN'ALTRA COSA, E NESSUNO L'AVEVA SCRITTO: buttava via
    // TUTTA la SPA — cache di RTK Query (15 API: profili Limulo, `/v1/me`, iscrizioni, wallet…),
    // store Redux oltre `auth`, memo e singleton di modulo. Tolta la navigazione (#368), la SPA
    // sopravviveva al logout e l'account B ereditava lo stato in memoria dell'account A.
    // 📌 Reperto dal vivo, stg 2026-09-24 21:06Z: dopo cambi di account nella stessa mini app,
    //   `422 profile.required` all'iscrizione con il profilo presente in Mongo, e da script con
    //   `profileId` la stessa iscrizione passa (201) — [dedotto, non misurato nel client] il
    //   `profileId` veniva da una lista profili vecchia. E' il guasto d'identita' della
    //   sezione Limulo del CLAUDE.md, arrivato da un'altra porta.
    // ⇒ In Telegram, finita la pulizia, si RICARICA la pagina: e' la meta' della cura che la
    //   navigazione faceva per caso, ora dichiarata. Lezione: togliere un effetto collaterale
    //   toglie anche i suoi benefici non dichiarati.
    // ⚠️ La ricarica sta QUI, dopo `clearStoredTokens()` e non prima: una ricarica che parte
    //   prima della pulizia lascerebbe a una scrittura tardiva (SDK, refresh in volo) il tempo di
    //   ri-seminare lo storage che la pagina nuova rileggerebbe al boot.
    // Dopo la ricarica: nessun token ⇒ `hasStoredSession()` falso ⇒ `bridgeAuthenticated`
    // nasce falso e il gate mostra <Login />. Un vecchio `auth_<code>` ancora nello
    // `start_param` NON viene ri-scambiato: `LAST_CONSUMED_AUTH_CODE` da solo non basterebbe
    // (lo cancella proprio `clearStoredTokens()`, per il login successivo), ma la stessa
    // pulizia toglie il verifier PKCE e `exchangeAuthCode` si ferma senza rete
    // (`tests/logout-ricarica-riparte-da-login.test.ts`).
    // ⛔ Niente `resetApiState` qui «per cintura»: la ricarica azzera tutte e tre le specie di
    //   stato, `resetApiState` una sola — se la ricarica mancasse, slice e singleton
    //   resterebbero dell'account A lo stesso, quindi non sarebbe un ripiego ma una copertura
    //   parziale con l'aria di una completa. E sarebbe una seconda lista delle API da tenere
    //   allineata a mano con `store/index.ts`.
    // Fuori da Telegram niente ricarica: `signOut()` naviga gia', e navigando resetta.
    // 🔴 La sessione Limulo si REVOCA sul server prima che la pulizia locale tolga il refresh token
    // che serve a farlo (`revocaEChiudiLimuloSession`, `lib/limulo/session.ts`): la POST parte
    // adesso, di sincrono, e l'esito non decide niente del logout. La promessa non rifiuta mai;
    // la si attende solo prima della ricarica, che altrimenti abbandonerebbe la richiesta in volo.
    const revocaLimulo = revocaEChiudiLimuloSession();
    const pulisciSdk = inTelegram ? clearAllTokens() : signOut();
    void pulisciSdk
      .catch(() => {
        /* SDK may throw if it had no session — ignore. */
      })
      .then(() => revocaLimulo)
      .finally(() => {
        clearStoredTokens();
        if (inTelegram) window.location.reload();
      });
    // Also clear synchronously up-front: if `signOut()` never resolves (older
    // SDK / no session), the bridge tokens are still gone immediately and the
    // AppShell gate (driven by Redux above) already shows <Login />.
    clearStoredTokens();
  }, [inTelegram, signOut, clearAllTokens, dispatch]);

  return {
    isAuthenticated,
    isLoading: effectiveLoading,
    signIn: triggerSignIn,
    signOut: triggerSignOut,
  };
}
